import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import PageHeader from '../../../components/PageHeader'
import Accordion from '../../../components/v2/accordions/Accordion'
import Checkbox from '../../../components/v2/buttons/Checkbox'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import DialogModal from '../../../components/v2/modals/DialogModal'
import Title from '../../../components/v2/Title'
import { useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { IntTplState, setUserField } from '../../../store/internationalTplSlice'
import { useInternationalTplPages } from './useInternationalTplPages'
import { useInternationalTplProgress } from './useInternationalTplProgress'

const InternationalTplCheckInfo = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [franiTersModalIsOpen, setFraniTersModalIsOpen] = useState<boolean>(false)
  const [inurerCompanyTermsModalIsOpen, setInurerCompanyTermsModalIsOpen] = useState<boolean>(false)
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const { checkout, filter } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetIntTplPageInfoQuery('international-tpl-check-info')

  // Use the hook to get pages and page data from Redux
  const { pages: pagesFromHook } = useInternationalTplPages()
  const { totalCount, passedCount, pages: progressPages } = useInternationalTplProgress()
  const displayPages = progressPages.length > 0 ? progressPages : pagesFromHook

  // Get product category for information sheet
  const { data: productCategories, isLoading: isProductCategoryLoading } =
    api.useGetProductCategoriesListQuery(undefined)

  const categoryId = productCategories?.find((x) => x.slug === 'international-tpl')?.id || 0

  const { data: informationsheetlist, isLoading: isInformationSheetListLoading } = api.useGetInformationSheetQuery({
    categoryId: categoryId,
    attachmentSlug: 'information-sheet',
  })

  const itemID = informationsheetlist?.find((x) => x.productCategoryId === categoryId)?.id || 0

  const { data: currentInformationSheet, isLoading: isCurrentInformationSheetLoading } =
    api.useGetInformationSheetDetailQuery(itemID, {
      skip: !itemID && true,
    })

  const [getClientTypes, { data: client, isLoading: isClientLoading }] = api.useGetIntTplClientTypeMutation()

  const [getVehicleOptions, { data: vehicleOptions, isLoading: isVehicleOptionsLoading }] =
    api.useGetIntTplVehicleOptionMutation()

  const [getVehicleModels, { data: vehicleModels, isLoading: isVehicleModelsLoading }] =
    api.useGetIntTplVehicleModelsMutation()

  const _clientTypeId =
    filter.isoCode === 'AM'
      ? client?.clientTypes?.find((x) => x?.slug === checkout?.user?.clientType)?.id
      : client?.clientTypes?.find((x) => x?.slug === 'individual')?.id

  const { data: countries, isLoading: isCountriesLoading } = api.useGetCountriesQuery(undefined)

  const registrationCountry = countries?.find((x) => x.isoCode === checkout.vehicle.registrationCountry)
  const citizenshipCountry = countries?.find((x) => x.isoCode === checkout.user?.cityzenship)

  useEffect(() => {
    if (filter && filter.isoCode && filter.day) {
      const _getDaysRequest = async () => {
        try {
          await getClientTypes({ countryCode: filter.isoCode, insuranceDays: filter.day as number }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_getDaysRequest) (single)')
          }
        }
      }
      // call request
      _getDaysRequest()
    }
    return () => {}
  }, [filter, getClientTypes])

  useEffect(() => {
    // get vehicle options
    if (filter && filter.isoCode && filter.day && _clientTypeId) {
      const _geVehicleOptionRequest = async () => {
        try {
          await getVehicleOptions({
            countryCode: filter.isoCode,
            insuranceDays: filter.day as number,
            clientTypeId: _clientTypeId,
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_geVehicleOptionRequest) (single)')
          }
        }
      }
      // call request
      _geVehicleOptionRequest()
    }
    return () => {}
  }, [filter, getVehicleOptions, _clientTypeId])

  useEffect(() => {
    // get vehicle models
    if (filter && filter.isoCode && checkout?.vehicle?.vehicleId) {
      const _geVehicleModelsRequest = async () => {
        try {
          await getVehicleModels({
            countryCode: filter.isoCode,
            vehicleMarkId: checkout.vehicle.vehicleId as number,
          }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_geVehicleModelsRequest) (single)')
          }
        }
      }
      // call request
      _geVehicleModelsRequest()
    }
    return () => {}
  }, [filter, getVehicleModels, checkout.vehicle.vehicleId])

  const getFileNameFromUrl = (url: string) => {
    const fullName = url.split('/').pop()

    const parts = fullName?.split('-')
    if (parts && parts.length >= 2) {
      return parts.slice(-2).join('-')
    }
    return fullName
  }

  const closeFraniTersModal = () => {
    setFraniTersModalIsOpen(false)
  }

  const closeInsurerCompanyModal = () => {
    setInurerCompanyTermsModalIsOpen(false)
  }

  const CheckboxText = `${t('iReadAndAgree')} ${checkout?.offer?.companyTitle} ${lang === 'ge' ? ' - ს' : "'s"} ${t(
    'insuranceCondition',
  )}`

  // auto redirect first page
  useEffect(() => {
    if (!filter?.isoCode) {
      navigate('/international-tpl-country')
    }
    return () => {}
  }, [filter?.isoCode, navigate])

  return isClientLoading ||
    isVehicleOptionsLoading ||
    isVehicleModelsLoading ||
    isPageInfoLoading ||
    isProductCategoryLoading ||
    isInformationSheetListLoading ||
    isCurrentInformationSheetLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      {/* frani terms modal */}
      <DialogModal
        title={`${t('franiConditions')}`}
        isOpen={franiTersModalIsOpen}
        onClose={closeFraniTersModal}
        src={currentInformationSheet?.attachment?.[lang]?.url || ''}
        isPdf={true}
        acceptOnClick={() => [
          dispatch(
            setUserField({
              fieldName: 'privacyTerm',
              fieldValue: true,
            }),
          ),
          closeFraniTersModal(),
        ]}
        //
        declineOnClick={() => [
          dispatch(
            setUserField({
              fieldName: 'privacyTerm',
              fieldValue: false,
            }),
          ),
          closeFraniTersModal(),
        ]}
      />
      {/* frani terms modal */}

      {/* insurance company terms */}
      <DialogModal
        title={CheckboxText}
        isOpen={inurerCompanyTermsModalIsOpen}
        onClose={closeInsurerCompanyModal}
        src={checkout?.offer?.policyTermsUrl || ''}
        isPdf={true}
        acceptOnClick={() => [
          dispatch(
            setUserField({
              fieldName: 'policyTerm',
              fieldValue: true,
            }),
          ),
          closeInsurerCompanyModal(),
        ]}
        //
        declineOnClick={() => [
          dispatch(
            setUserField({
              fieldName: 'policyTerm',
              fieldValue: false,
            }),
          ),
          closeInsurerCompanyModal(),
        ]}
      />
      {/* insurance company terms */}
      <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
      <Container>
        <Content>
          <Title text={pageInfo?.translations?.[lang]?.title} />
          <Block>
            {checkout.user.clientType === 'individual' ? (
              <Accordion
                editBtnOnchange={() => navigate('/international-tpl-personal-info')}
                editBtnText={t('edit')}
                isOpen={false}
                title={t('yourPersonalInfo')}
              >
                <TextDiv>
                  <div>{t('cityzenship')}</div>
                  <div>{citizenshipCountry?.translations?.[lang]?.name}</div>
                </TextDiv>

                {(() => {
                  const cityzenship = checkout.user?.cityzenship
                  const cityzenshipIso = typeof cityzenship === 'string' ? cityzenship : undefined
                  return cityzenshipIso?.toLowerCase() === 'am'
                })() && (
                  <TextDiv>
                    <div>{t('socialSecurityNumber')}</div>
                    <div>{checkout.user?.socialSecurityNumber}</div>
                  </TextDiv>
                )}

                <TextDiv>
                  <div>{t('personalNumber')}</div>
                  <div>{checkout.user?.idNumber}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('dateOfBirth')}</div>
                  <div>{checkout?.user?.dateOfbirth as never as string}</div>
                </TextDiv>

                {checkout.user?.translations?.ge?.firstName && (
                  <TextDiv>
                    <div>{t('nameInGeorgia')}</div>
                    <div>{checkout.user?.translations?.ge?.firstName}</div>
                  </TextDiv>
                )}

                {checkout.user?.translations?.ge?.lastName && (
                  <TextDiv>
                    <div>{t('surnameInGeorgia')}</div>
                    <div>{checkout.user?.translations?.ge?.lastName}</div>
                  </TextDiv>
                )}

                <TextDiv>
                  <div>{t('nameInEnglish')}</div>
                  <div>{checkout.user?.translations?.en?.firstName}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('surnameInEnglish')}</div>
                  <div>{checkout.user?.translations?.en?.lastName}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('gender')}</div>
                  <div>
                    {checkout.user?.gender === 'female' && `${t('female')}`}
                    {checkout.user?.gender === 'male' && `${t('male')}`}
                  </div>
                </TextDiv>

                <TextDiv>
                  <div>{t('email')}</div>
                  <div>{checkout.user?.clientEmail}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('phone')}</div>
                  <div>+{checkout.user?.clientPhone}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('documentType')}</div>
                  <div style={{ whiteSpace: 'nowrap' }}>
                    {
                      client?.clientDocumentTypes?.find((x) => x.id === checkout.user?.clientDocTypeId)?.translations?.[
                        lang
                      ]?.name
                    }
                  </div>
                </TextDiv>

                {checkout.user?.clientDocOneSide ? (
                  <TextDiv>
                    <div></div>
                    <NavigateLink onClick={() => window.open(checkout.user?.clientDocOneSide, '_blank')}>
                      {getFileNameFromUrl(checkout.user?.clientDocOneSide)}
                    </NavigateLink>
                  </TextDiv>
                ) : null}

                {checkout.user?.clientDocSecondSide ? (
                  <TextDiv>
                    <div></div>
                    <NavigateLink onClick={() => window.open(checkout.user?.clientDocSecondSide, '_blank')}>
                      {getFileNameFromUrl(checkout.user?.clientDocSecondSide)}
                    </NavigateLink>
                  </TextDiv>
                ) : null}

                {checkout.user?.clientDocThirdSide ? (
                  <TextDiv>
                    <div></div>
                    <NavigateLink onClick={() => window.open(checkout.user?.clientDocThirdSide, '_blank')}>
                      {getFileNameFromUrl(checkout.user?.clientDocThirdSide)}
                    </NavigateLink>
                  </TextDiv>
                ) : null}
              </Accordion>
            ) : (
              <Accordion
                editBtnOnchange={() => navigate('/international-tpl-personal-info')}
                editBtnText={t('edit')}
                isOpen={false}
                title={t('yourPersonalInfo')}
              >
                <TextDiv>
                  <div>{t('countryOfRegistration')}</div>
                  <div>{registrationCountry?.translations?.[lang]?.name}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('identificationNumber')}</div>
                  <div>{checkout.user?.identificationNumber}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('legalEntityType')}</div>
                  <div>
                    {
                      client?.clientSubtypes?.find((x) => x.ligaApiId === checkout.user?.legalEntityType)
                        ?.translations?.[lang]?.name
                    }
                  </div>
                </TextDiv>

                <TextDiv>
                  <div>{t('_companyName')}</div>
                  <div>{checkout.user?.companyName}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('representativeName')}</div>
                  <div>{checkout.user?.representativeName}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('representativeSurname')}</div>
                  <div>{checkout.user?.representativeSurname}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('email')}</div>
                  <div>{checkout.user?.clientEmail}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('phone')}</div>
                  <div>+{checkout.user?.clientPhone}</div>
                </TextDiv>

                <TextDiv>
                  <div>{t('documentType')}</div>
                  <div style={{ whiteSpace: 'nowrap' }}>
                    {
                      client?.clientDocumentTypes?.find((x) => x.id === checkout.user?.clientDocTypeId)?.translations?.[
                        lang
                      ]?.name
                    }
                  </div>
                </TextDiv>

                {checkout.user?.clientDocOneSide ? (
                  <TextDiv>
                    <div></div>
                    <NavigateLink onClick={() => window.open(checkout.user?.clientDocOneSide, '_blank')}>
                      {getFileNameFromUrl(checkout.user?.clientDocOneSide)}
                    </NavigateLink>
                  </TextDiv>
                ) : null}

                {checkout.user?.clientDocSecondSide ? (
                  <TextDiv>
                    <div></div>
                    <NavigateLink onClick={() => window.open(checkout.user?.clientDocSecondSide, '_blank')}>
                      {getFileNameFromUrl(checkout.user?.clientDocSecondSide)}
                    </NavigateLink>
                  </TextDiv>
                ) : null}

                {checkout.user?.clientDocThirdSide ? (
                  <TextDiv>
                    <div></div>
                    <NavigateLink onClick={() => window.open(checkout.user?.clientDocThirdSide, '_blank')}>
                      {getFileNameFromUrl(checkout.user?.clientDocThirdSide)}
                    </NavigateLink>
                  </TextDiv>
                ) : null}
              </Accordion>
            )}
            {/* <Accordion hideEditBtn={true} isOpen={false} title={t('carOwnerInformation')}>
              <TextDiv>
                <div>{t('personalNumber')}</div>
                <div>{checkout.user.idNumber}</div>
              </TextDiv>
            </Accordion> */}

            <Accordion
              editBtnOnchange={() => navigate('/international-tpl-car-info')}
              editBtnText={t('edit')}
              isOpen={false}
              title={t('carInfo')}
            >
              <TextDiv>
                <div>{t('countryOfRegistration')}</div>
                <div>{registrationCountry?.translations?.[lang]?.name}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('registerNumber')}</div>
                <div>{checkout.vehicle?.registrationNumber?.toUpperCase()}</div>
              </TextDiv>

              {checkout.vehicle.codeType === 'vin' && (
                <TextDiv>
                  <div>{t('vinCode')}</div>
                  <div>{checkout.vehicle?.code}</div>
                </TextDiv>
              )}

              {checkout.vehicle.codeType === 'chassisNumber' && (
                <TextDiv>
                  <div>{t('chassisNumber')}</div>
                  <div>{checkout.vehicle?.code}</div>
                </TextDiv>
              )}

              {checkout.vehicle.codeType === 'bodyNumber' && (
                <TextDiv>
                  <div>{t('bodyNumber')}</div>
                  <div>{checkout.vehicle?.code}</div>
                </TextDiv>
              )}

              <TextDiv>
                <div>{t('usageType')}</div>
                <div>
                  {
                    vehicleOptions?.usageTypes?.find((x) => x.ligaApiId === checkout?.vehicle?.usageId)?.translations?.[
                      lang
                    ]?.name
                  }
                </div>
              </TextDiv>

              <TextDiv>
                <div>{t('carType')}</div>
                <div>
                  {
                    vehicleOptions?.vehicleTypes?.find((x) => x.ligaApiId === checkout?.vehicle?.vehicleTypeId)
                      ?.translations?.[lang]?.name
                  }
                </div>
              </TextDiv>

              <TextDiv>
                <div>{t('brand')}</div>
                <div>{vehicleOptions?.vehicleMarks?.find((x) => x.id === checkout?.vehicle?.vehicleId)?.name}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('carModel')}</div>
                <div>{vehicleModels?.find((x) => x.id === checkout?.vehicle?.vehicleModelId)?.name}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('dateOfIssue')}</div>
                <div>{checkout.vehicle?.manufactureYear}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('engineCapacity')}</div>
                <div>{checkout.vehicle?.engineCapacity}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('color')}</div>
                <div>{checkout.vehicle?.color}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('documentType')}</div>
                <div>
                  {
                    vehicleOptions?.vehicleDocumentTypes?.find((x) => x.id === checkout?.vehicle?.documentTypeId)
                      ?.translations?.[lang]?.name
                  }
                </div>
              </TextDiv>

              <TextDiv>
                <div>{t('documentNumber')}</div>
                <div>{checkout.vehicle.documentNumber}</div>
              </TextDiv>

              {checkout.vehicle?.vehicleDocOneSide ? (
                <TextDiv>
                  <div></div>
                  <NavigateLink onClick={() => window.open(checkout.vehicle?.vehicleDocOneSide, '_blank')}>
                    {getFileNameFromUrl(checkout.vehicle?.vehicleDocOneSide)}
                  </NavigateLink>
                </TextDiv>
              ) : null}

              {checkout.vehicle?.vehicleDocSecondSide ? (
                <TextDiv>
                  <div></div>
                  <NavigateLink onClick={() => window.open(checkout.vehicle?.vehicleDocSecondSide, '_blank')}>
                    {getFileNameFromUrl(checkout.vehicle?.vehicleDocSecondSide)}
                  </NavigateLink>
                </TextDiv>
              ) : null}
            </Accordion>

            <Accordion hideEditBtn={true} isOpen={true} title={t('policyInfo')}>
              <TextDiv>
                <div>{t('insuranceCompany')}</div>
                <img style={{ height: '12px' }} src={checkout?.offer?.iconUrl} alt="insurer-icon" />
              </TextDiv>

              <TextDiv>
                <div>{t('teritorialCoverage')}</div>
                <div>{checkout?.offer?.territorialCoverage}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('usageType')}</div>
                <div>{checkout?.offer?.vehicleUsage}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('startDate')}</div>
                <div>{dayjs(checkout?.offer?.startDate).format('D MMMM YYYY')}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('numberOfDays')}</div>
                <div>
                  {filter?.day} {t('day')}
                </div>
              </TextDiv>

              <TextDiv>
                <div>{t('paymentSchedule')}</div>
                <div>{t('annualPayment')}</div>
              </TextDiv>

              <TextDiv>
                <div>{t('currentPayment')}</div>
                <div>{checkout?.offer?.premium?.toFixed(2)} ₾</div>
              </TextDiv>

              <HR />

              <Checkbox
                id="international-tpl-check-information-privacy-terms"
                label={`${t('franiConditions')}`}
                checked={checkout.user.privacyTerm || false}
                defaultChecked={checkout.user.privacyTerm || false}
                onClick={() => setFraniTersModalIsOpen(true)}
              />

              <HR />

              <Checkbox
                id="international-tpl-check-information-company-terms"
                label={CheckboxText}
                checked={checkout.user.policyTerm || false}
                defaultChecked={checkout.user.policyTerm || false}
                onClick={() => setInurerCompanyTermsModalIsOpen(true)}
              />
            </Accordion>
          </Block>
          <Buttons>
            <NavigationButton
              text={`${t('back')}`}
              buttonType="prev"
              onClick={() => navigate('/international-tpl-offer')}
            />
            <NavigationButton
              text={`${t('next')}`}
              buttonType="next"
              disabled={!(checkout.user.policyTerm === true && checkout.user.privacyTerm === true)}
              onClick={() => navigate('/international-tpl-payment')}
            />
          </Buttons>
        </Content>
      </Container>
    </Wrapper>
  )
}

const NavigateLink = styled.a`
  cursor: pointer;
  text-decoration: underline;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`
const HR = styled.hr`
  width: 100%;
`

const TextDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${Colors.nevadaGrey};
  font-size: 14px;
  margin-bottom: 14px;

  & > div:first-child {
    width: 60%;
  }

  & > div:last-child {
    text-align: right;
  }
`

const Content = styled.div`
  margin-top: 40px;
  margin-bottom: 32px;
  z-index: 2;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`
const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  max-width: 432px;
  width: 100%;
  margin: 0 auto;
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`

export default InternationalTplCheckInfo
